import React, { useState, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { Breadcrumbs } from '@prisjakt/ui/breadcrumbs';
import { useAppContext } from 'src/components/app-context';
import { PageContent } from 'src/components/page';
import { Collapsible } from 'src/components/collapsible';
import type { AppState } from 'src/reducers';
import { DesktopOnly, MediumDown, MediumUp, MobileOnly } from 'src/ui/block';
import {
  trackInternalNavigationClick,
  InternalNavigationScope,
} from 'src/globals/tracking/tracking';
import PromoBanner from '../../components/campaign-banners/promo-banner';
import RecentlyVisitedProducts from '../../components/recently-visited';
import AdContainer from '../../components/ads';
import NoScroll from '../../components/no-scroll';
import WordCloud from '../../components/word-cloud';
import type { Campaign, CampaignBanner, CategoryPageQuery } from '../../types';
import { CategoryList } from '../../components/category-list';
import CompareProductsBasket from '../../components/compare-products-basket';
import { useCompareProductsSelection } from '../../hooks/compare-products-selection';
import type { ChangeComparisonSelectionSource } from '../product-compare/tracking';
import { setHorizontalFilter } from './actions/filter';
import SubCategoryListing from './components/sub-category-listing';
import VisibilityToggle from './components/sub-category-listing/components/toggle';
import { HorizontalFilter } from './components/filter';
import { FilterMobileToggle } from './components/filter/components/mobile-toggle';
import { NewCategoryProducts } from './components/new-category-products';
import { CategoryProductListing } from './category-product-listing';
import { CategoryArticles } from './category-articles';
import type { DeepNonNullable, LayoutMode } from './types';
import './sagas/index';

import {
  BrandLink,
  BrandLogo,
  BreadCrumbWrapper,
  CategoryPageAdWrapper,
  CategoryPageGutter,
  CategoryPageHeader,
  CategoryPageHeaderAliases,
  CategoryPageHeaderDescription,
  CategoryPageHeaderMedia,
  CategoryPageProducts,
  CategoryPageProductWrapper,
  CategoryPageSubHeader,
  CategoryPageWrapper,
  Column,
  Filter,
} from './styles';
import { useMatchedRoute } from 'src/hooks/use-matched-route';
import { useCategoryPageQuery } from './queries/use-category-page-query';
import Loader from 'src/components/loader';

const isVirtualCategory = (categoryId: string) => /^v/.test(categoryId);

type CategoryProps = {
  data: DeepNonNullable<CategoryPageQuery>;
};

export const CategoryLoader = () => {
  const route = useMatchedRoute();
  const { data, isLoading, isSuccess } = useCategoryPageQuery(route!.variables);

  if (!isLoading && !isSuccess) {
    return null;
  }

  return (
    <PageContent withAds data-test="CategoryPage">
      {isLoading ? <Loader /> : null}
      {isSuccess && data.category ? (
        <Category data={data as DeepNonNullable<CategoryPageQuery>} />
      ) : null}
    </PageContent>
  );
};

const Category: React.FC<
  React.PropsWithChildren<React.PropsWithChildren<CategoryProps>>
> = props => {
  const [subCategoriesCollapsed, setSubCategoriesCollapsed] = useState(true);
  const toggleSubCategoriesCollapsed = useCallback(
    () => setSubCategoriesCollapsed(!subCategoriesCollapsed),
    [subCategoriesCollapsed]
  );
  const { t, linkTo } = useAppContext();
  const compareProductSelection = useCompareProductsSelection();

  const dispatch = useDispatch();

  const categorySettings = useSelector(
    (state: AppState) => state.areas.category
  );
  const campaignBanners = useSelector(
    (state: AppState) =>
      state.globals.campaignBanners as {
        active: (CampaignBanner & {
          campaign: Pick<Campaign, 'media'>;
        })[];
      }
  );

  const showHorizontalFilter = useSelector(
    (state: AppState) => state.areas.category.filter.showHorizontalFilter
  );

  const {
    id,
    immediateProducts,
    products,
    categories,
    brand,
    brands,
    name,
    aliases,
    description,
    logo,
    path,
    pathName,
    defaultLayoutMode,
    productCount,
    storesCount,
    categoryTree,
    articles,
    popularSearchTerms,
  } = props.data.category;
  const [categoryIsCollapsed, setCategoryIsCollapsed] = useState(true);

  const abortProductSelectionFromBasket = useCallback(
    () => compareProductSelection.abortProductSelection('basket'),
    [compareProductSelection]
  );

  const deselectProductById = useCallback(
    (productId: number, source: string) => {
      const deselectedProduct = products.nodes.find(p => p.id === productId);
      if (deselectedProduct) {
        compareProductSelection.deselectProduct(
          source as ChangeComparisonSelectionSource,
          deselectedProduct
        );
      }
    },
    [products, compareProductSelection]
  );

  // Return 404 on empty brand category pages to properly sunset
  // indexed pages that become empty when category tree is reorganized
  if (!immediateProducts && brand && !products?.nodes?.length) {
    const error = new Error('Page not found');
    (error as any).status = 404; // TODO: Fix any
    (error as any).level = 'warn';
    throw error;
  }

  const toggleHorizontalFilter = useCallback(() => {
    dispatch(setHorizontalFilter(!showHorizontalFilter));
  }, [dispatch, showHorizontalFilter]);

  const mode =
    (!immediateProducts && 'grid') ||
    (categorySettings.category && categorySettings.category.mode) ||
    defaultLayoutMode;

  const { mobileOpen, query, error: filterError } = categorySettings.filter;

  const hasSubCategories = categories && categories.nodes.length > 0;
  const hasSubBrandCategories =
    !isVirtualCategory(id) && brands && brands.nodes.length > 0;
  const brandCategories = hasSubBrandCategories
    ? brands.nodes.map(categoryBrand => ({
        id: `${id}:${categoryBrand.id}`,
        name: `${categoryBrand.name} ${name}`,
        pathName: linkTo('brandCategory', {
          id,
          pathName,
          brandId: categoryBrand.id,
        }),
      }))
    : [];

  const campaignPromoBanners = {
    before:
      campaignBanners &&
      campaignBanners.active.filter(b => b && b.type === 'BEFORE_PROMO_BANNER'),
    during:
      campaignBanners &&
      campaignBanners.active.filter(b => b && b.type === 'DURING_PROMO_BANNER'),
  };
  const campaignBannerLinkParams = { category: id };

  const showFilter = immediateProducts && !filterError;

  const categoryDescription = useMemo(
    () => (
      <CategoryPageHeaderDescription data-test="CategoryDescription">
        <span
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: description }}
        />
        {aliases && aliases.length > 0 && (
          <CategoryPageHeaderAliases>
            {t('category', 'category.alias', {
              alias: aliases.join(', '),
            })}
          </CategoryPageHeaderAliases>
        )}
      </CategoryPageHeaderDescription>
    ),
    [aliases, description, t]
  );

  return (
    <>
      {campaignPromoBanners.during &&
        campaignPromoBanners.during.length > 0 && (
          <CategoryPageGutter>
            <PromoBanner
              banners={campaignPromoBanners.during}
              linkParams={campaignBannerLinkParams}
            />
          </CategoryPageGutter>
        )}
      <BreadCrumbWrapper>
        <Breadcrumbs
          containerProps={{
            onClick: (nextPage: string) => {
              trackInternalNavigationClick({
                scope: InternalNavigationScope.BREADCRUMB,
                nextPage,
              });
            },
          }}
        >
          <Link data-test="Crumb" to="/">
            {t('common', 'breadcrumb.home')}
          </Link>
          {path.map((categoryPath, index) => {
            return path.length - 1 === index ? (
              <span data-test="Crumb">{categoryPath.name}</span>
            ) : (
              <Link
                data-test="Crumb"
                key={categoryPath.id}
                to={categoryPath.pathName}
              >
                {categoryPath.name}
              </Link>
            );
          })}
        </Breadcrumbs>
      </BreadCrumbWrapper>
      <CategoryPageWrapper>
        <div>
          <CategoryPageHeader
            media={
              logo && (
                <CategoryPageHeaderMedia width={100} alt={name} src={logo} />
              )
            }
            title={name}
            right={
              brand && brand.featured && brand.logo ? (
                <BrandLink to={linkTo('brand', brand)}>
                  <BrandLogo src={brand.logo} alt={brand.name} />
                </BrandLink>
              ) : null
            }
          >
            {/* Don't show the category description on brand category pages */}
            {!brand && (
              <>
                <MobileOnly>
                  <Collapsible
                    collapsed={categoryIsCollapsed}
                    expandText={t('symbols', 'symbol.expand')}
                    onExpand={() => setCategoryIsCollapsed(false)}
                  >
                    {categoryDescription}
                  </Collapsible>
                </MobileOnly>

                <MediumUp>{categoryDescription}</MediumUp>
              </>
            )}
          </CategoryPageHeader>
        </div>

        {(hasSubCategories || hasSubBrandCategories) && immediateProducts && (
          <DesktopOnly>
            <CategoryPageGutter>
              <SubCategoryListing
                styling="inline-list"
                categories={categories}
                brands={brandCategories}
              />
            </CategoryPageGutter>
          </DesktopOnly>
        )}

        {hasSubCategories && !immediateProducts && categoryTree && (
          <div>
            <CategoryPageGutter>
              <>
                <CategoryList categories={categoryTree} withAds />

                <CategoryPageAdWrapper>
                  <AdContainer
                    scalable
                    scaleBase=".category-page--adWrapper"
                    placement="panorama_extra"
                    format="panorama_1_wde"
                    min={861}
                    max={10000}
                    spacing={{ top: '0', bottom: '10px' }}
                  />
                </CategoryPageAdWrapper>
              </>
            </CategoryPageGutter>
          </div>
        )}

        {showFilter && !mobileOpen && (
          <CategoryPageSubHeader>
            <Column fullWidth={!hasSubCategories}>
              <FilterMobileToggle filterSettings={query} />
            </Column>
            {hasSubCategories && (
              <>
                <MediumDown>
                  <Column>
                    <VisibilityToggle
                      active={!subCategoriesCollapsed}
                      onToggle={toggleSubCategoriesCollapsed}
                      label={t('category', 'subcategories.show')}
                    />
                  </Column>
                </MediumDown>

                <MediumDown>
                  <Column fullWidth>
                    <SubCategoryListing
                      styling="list"
                      categories={categories}
                      collapse={subCategoriesCollapsed}
                    />
                  </Column>
                </MediumDown>
              </>
            )}
          </CategoryPageSubHeader>
        )}

        <>
          {showFilter && showHorizontalFilter && (
            <DesktopOnly>
              <div>
                <HorizontalFilter
                  brand={brand}
                  productCount={productCount}
                  canCompare={mode === 'row'}
                  quickFilters={[]}
                  toggleFilter={toggleHorizontalFilter}
                  expanded={showHorizontalFilter}
                />
              </div>
            </DesktopOnly>
          )}

          {(products.nodes.length > 0 || immediateProducts) && (
            <CategoryPageGutter>
              <CategoryPageProductWrapper>
                {showFilter && (
                  <>
                    <MediumDown>
                      <NoScroll mobile enabled={mobileOpen} />
                    </MediumDown>
                    <MediumDown>
                      <Filter
                        open={mobileOpen}
                        categoryId={id}
                        brand={brand}
                        productCount={productCount}
                        canCompare={mode === 'row'}
                        quickFilters={[]}
                        toggleFilter={toggleHorizontalFilter}
                      />
                    </MediumDown>
                  </>
                )}
                <CategoryPageProducts
                  data-test="ProductListing"
                  data-test-category={id}
                >
                  <CategoryProductListing
                    productCount={productCount}
                    products={products}
                    defaultLayoutMode={defaultLayoutMode as LayoutMode}
                    immediateProducts={immediateProducts}
                    storesCount={storesCount}
                    categoryName={name}
                    showHorizontalFilter={showHorizontalFilter}
                    logo={logo}
                    compareProductSelection={compareProductSelection}
                  />
                  {compareProductSelection.isSelectingProducts && (
                    <CompareProductsBasket
                      products={compareProductSelection.selectedProducts}
                      compareUrl={
                        compareProductSelection.selectedProducts.length > 1
                          ? linkTo('productCompare', {
                              category: props.data.category,
                              products:
                                compareProductSelection.selectedProducts,
                            })
                          : undefined
                      }
                      onEmpty={abortProductSelectionFromBasket}
                      onProductDelete={deselectProductById}
                    />
                  )}
                </CategoryPageProducts>
              </CategoryPageProductWrapper>
            </CategoryPageGutter>
          )}
        </>

        {campaignPromoBanners.before &&
          campaignPromoBanners.before.length > 0 && (
            <CategoryPageGutter>
              <PromoBanner inverted banners={campaignPromoBanners.before} />
            </CategoryPageGutter>
          )}

        <CategoryArticles articles={articles} />

        <CategoryPageGutter>
          <NewCategoryProducts categoryId={id} />

          <RecentlyVisitedProducts />
        </CategoryPageGutter>
      </CategoryPageWrapper>
      {(immediateProducts || !brand) &&
      Array.isArray(popularSearchTerms) &&
      popularSearchTerms.length ? (
        <WordCloud
          title={t('search', 'search.related-searches')}
          items={popularSearchTerms.slice(0, 100)}
          showMoreUrl={linkTo('keywordListing', props.data.category)}
          numberOfVisibleItems={35}
        />
      ) : null}
    </>
  );
};

export default CategoryLoader;
